<template>
  <GloboGraphicsModulo
    icon="mdi-playlist-play"
    title="Playlists"
    collapsible
    :offline="offline"
  >
    <!-- Subheader: playlist select + create / config accent buttons -->
    <template #subheader>
      <div class="gg-pl-sub">
        <v-select
          v-model="selectedPlaylistId"
          :items="playlistItems"
          :disabled="playlistItems.length === 0"
          item-text="text"
          item-value="value"
          dense
          solo
          flat
          hide-details
          :placeholder="playlistItems.length === 0 ? 'Nenhuma playlist disponível' : 'Selecione uma playlist disponível'"
          :class="[playlistItems.length === 0 ? 'gg-pl-select--disabled' : '']"
          append-icon="mdi-menu-down"
          class="gg-pl-select"
          :menu-props="{ contentClass: 'gg-select-menu', offsetY: true, nudgeBottom: 4 }"
        >
          <template v-slot:selection="{ item }">
            <span class="gg-pl-select__val">{{ item.text }}</span>
            <span v-if="item.ativa" class="gg-pl-select__badge">Ativa</span>
          </template>
          <template v-slot:item="{ item }">
            <span class="gg-pl-select__opt">{{ item.text }}</span>
            <span v-if="item.ativa" class="gg-pl-select__badge">Ativa</span>
          </template>
        </v-select>
        <!-- Activate sits with the select it acts on, ahead of the two icon buttons.
             Only shown once a playlist is chosen — there is nothing to activate before
             that, and an always-present disabled button reads as broken. -->
        <template v-if="selectedPlaylistId">
          <button
            v-if="!isActive"
            type="button"
            class="gg-pl-activate"
            :disabled="loading"
            @click="handleActivate()"
          >
            Ativar
          </button>
          <button
            v-else
            type="button"
            class="gg-pl-activate gg-pl-activate--stop"
            :disabled="loading"
            @click="handleDeactivate"
          >
            Desativar
          </button>
        </template>
        <button
          type="button"
          class="gg-pl-iconbtn"
          title="Importar MOS playlist"
          @click="showMosPicker = true"
        >
          <v-icon size="24">mdi-folder-upload</v-icon>
        </button>
        <v-menu offset-y nudge-bottom="4" content-class="gg-select-menu">
          <template v-slot:activator="{ on, attrs }">
            <button
              type="button"
              class="gg-pl-iconbtn"
              title="Mais ações"
              v-bind="attrs"
              v-on="on"
            >
              <v-icon size="24">mdi-dots-vertical</v-icon>
            </button>
          </template>
          <v-list dense>
            <v-list-item @click="showCreateDialog = true">
              <v-icon small class="mr-2">mdi-playlist-plus</v-icon>
              <span class="gg-pl-select__opt">Criar nova playlist</span>
            </v-list-item>
            <v-list-item v-if="selectedPlaylistId && !isMos" @click="handleCreateGroup">
              <v-icon small class="mr-2">mdi-folder-plus</v-icon>
              <span class="gg-pl-select__opt">Criar nova pasta</span>
            </v-list-item>
            <v-list-item v-if="selectedPlaylistId" @click="showAddSceneDialog = true">
              <v-icon small class="mr-2">mdi-plus</v-icon>
              <span class="gg-pl-select__opt">Adicionar template ou cena</span>
            </v-list-item>
            <v-list-item v-if="selectedPlaylistId" @click="handleDeletePlaylist">
              <v-icon small class="mr-2">mdi-delete-outline</v-icon>
              <span class="gg-pl-select__opt">Excluir playlist</span>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </template>

    <MosRundownPicker
      v-model="showMosPicker"
      :cod-producao="cod_producao"
      @imported="onMosImported"
    />

    <!-- Empty: no playlist selected — the design's dark placeholder box. -->
    <div v-if="!selectedPlaylistId" class="gg-empty-box gg-empty-box--actions">
      <span class="gg-empty-box__text" >Nenhuma playlist selecionada</span>
      <div class="gg-empty-box__actions">
        <button type="button" class="gg-empty-btn" @click="showCreateDialog = true">
          Criar
        </button>
        <button
          type="button"
          class="gg-empty-btn gg-empty-btn--outlined"
          @click="showMosPicker = true"
        >
          Importar
        </button>
      </div>
    </div>

    <!-- Only the item list scrolls; the subheader above stays fixed. -->
    <div v-else class="gg-pl-scroll">
      <!-- Playlist Items — grouped (stories/subfolders), degrades to a flat list -->
      <PlaylistGroupedView
        v-if="currentPlaylist && currentPlaylist.items"
        :playlist="currentPlaylist"
        @drag-state-change="onDragStateChange"
        @reorder-items="onReorderSection"
        @move-item="handleMoveItem"
        @remove="handleRemoveItem"
        @take-item="handleTakeItem"
        @rename-group="handleRenameGroup"
        @delete-group="handleDeleteGroup"
        @reorder-groups="handleReorderGroups"
      />

      <!-- Error display -->
      <v-alert v-if="lastError" type="error" dense text class="mb-0 mt-2 text-caption">
        {{ lastError }}
      </v-alert>
    </div>

    <!-- Graphics the activation refused to publish. The API has always returned these,
         each with a message written for the operator; nothing rendered them, so a
         rundown could go live with none of it publishable behind a green "Ativada".
         A toast rather than an alert in the list: it holds while the condition does
         (like the offline one), and being fixed it cannot end up below the fold of a
         112-item rundown. -->
    <transition name="gg-toast-fade">
      <GloboGraphicsToast
        v-if="activationRefusals.length"
        variant="warning"
        :text="activationRefusalText"
        action-label="Dispensar"
        @action="dismissActivationReport"
      />
    </transition>

    <!-- Create Playlist drawer — name + optional starting cenas -->
    <GloboGraphicsCreatePlaylistDrawer
      v-model="showCreateDialog"
      :cod-producao="cod_producao"
      @created="onPlaylistCreated"
    />

    <!-- Engine busy — conflict confirmation -->
    <ModalConfirmAction
      v-if="engineBusyConflict"
      :visible="showEngineBusyModal"
      title="Engine ocupado"
      :message="engineBusyMessage"
      confirm-text="Ativar mesmo assim"
      confirm-color="red"
      :loading="loading"
      @confirm="handleForceActivate"
      @cancel="cancelEngineBusy"
    />

    <!-- Delete playlist confirmation -->
    <ModalConfirmAction
      :visible="showDeletePlaylistDialog"
      title="Excluir playlist"
      message="Tem certeza que deseja excluir esta playlist? Todos os itens serão removidos."
      confirm-text="Excluir"
      confirm-color="red"
      @confirm="confirmDeletePlaylist"
      @cancel="showDeletePlaylistDialog = false"
    />

    <!-- Adicionar template ou cena — picks from what the production already has -->
    <GloboGraphicsAddItemsDrawer
      v-model="showAddSceneDialog"
      :id-playlist="selectedPlaylistId"
      :cod-producao="cod_producao"
      :existing-ids="currentPlaylistCenaIds"
    />
  </GloboGraphicsModulo>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import GloboGraphicsModulo from "./GloboGraphicsModulo.vue";
import ModalConfirmAction from "@/components/Modal/ModalConfirmAction.vue";
import PlaylistGroupedView from "./playout/PlaylistGroupedView.vue";
import MosRundownPicker from "./playout/MosRundownPicker.vue";
import GloboGraphicsCreatePlaylistDrawer from "./GloboGraphicsCreatePlaylistDrawer.vue";
import GloboGraphicsAddItemsDrawer from "./GloboGraphicsAddItemsDrawer.vue";
import GloboGraphicsToast from "./GloboGraphicsToast.vue";

export default {
  name: "PlayoutPlaylistPanel",
  components: {
    GloboGraphicsModulo,
    ModalConfirmAction,
    PlaylistGroupedView,
    MosRundownPicker,
    GloboGraphicsCreatePlaylistDrawer,
    GloboGraphicsAddItemsDrawer,
    GloboGraphicsToast,
  },
  props: {
    cod_producao: { type: [Number, String], default: null },
    offline: { type: Boolean, default: false },
  },
  data() {
    return {
      selectedPlaylistId: null,
      showCreateDialog: false,
      showDeletePlaylistDialog: false,
      showMosPicker: false,
      // Drag state lives in PlaylistItemList; parent only tracks the boolean
      // so the polling watcher and refresh loop can pause during a drag.
      isDragging: false,
      persistingReorder: false,
      showAddSceneDialog: false,
      showEngineBusyModal: false,
      engineBusyConflict: null,
      refreshTimer: null,
      refreshFailureCount: 0,
      switchToken: 0,
    };
  },
  computed: {
    ...mapGetters({
      playlists: "playout/getterPlaylists",
      activePlaylist: "playout/getterActivePlaylist",
      playoutState: "playout/getterPlayoutState",
      vizrtHealth: "globographics/getterVizrtHealth",
      loading: "playout/getterPlayoutLoading",
      lastError: "playout/getterPlayoutError",
      activationReport: "playout/getterActivationReport",
      onAir: "playout/getterOnAir",
    }),

    // Graphics the activation refused to publish. The playlist IS live and the rest of
    // the rundown is takeable, so this is a warning about part of the rundown, never a
    // failed command — it deliberately does not share the `lastError` alert.
    activationRefusals() {
      return this.activationReport?.errors || [];
    },
    // What activation deferred to the background fill. Shown because `errors` only
    // covers the items published BEFORE the response: a refusal in the tail is not in
    // this list and is not reported anywhere the operator can see, so "2 failed" must
    // not be read as "the other 110 are fine".
    activationPending() {
      return this.activationReport?.publishing?.pending || 0;
    },
    activationTotal() {
      return this.activationReport?.publishing?.total || 0;
    },
    // "N de M" only when M is known and sane — an older API that answers `errors`
    // without `publishing` would otherwise render "2 de 0 gráficos".
    activationRefusalHeadline() {
      const n = this.activationRefusals.length;
      const total = this.activationTotal;
      const verb = n === 1 ? "não foi publicado" : "não foram publicados";
      return total >= n ? `${n} de ${total} gráficos ${verb}` : `${n} gráficos ${verb}`;
    },
    // Deliberately a summary, not the server's full message. That message names the
    // template and lists both field sets, which is the right level of detail for the
    // log and the wrong one for a toast: measured at 624px it runs to five lines, and
    // this toast is the same component as the offline one, which is built to hold two.
    // Nothing is lost that matters for air — the take gate still refuses the item with
    // FIELDS_UNMATCHED, so an unpublishable graphic cannot reach the engine on the
    // strength of a toast the operator skimmed.
    activationRefusalText() {
      const refusals = this.activationRefusals;
      const name = (r) => r.nome || `Item ${r.itemId}`;
      const shown = refusals.slice(0, 3).map(name).join(", ");
      const rest = refusals.length - 3;
      let text = `${this.activationRefusalHeadline}: ${shown}${rest > 0 ? ` e mais ${rest}` : ""}.`;
      // `errors` only covers the head of the rundown. A refusal in the background fill
      // is not in it and is reported nowhere, so "2 failed" must not read as "the rest
      // are fine".
      if (this.activationPending) {
        text += ` Outros ${this.activationPending} ainda estão sendo publicados em segundo plano.`;
      }
      return text;
    },
    mseHealthy() {
      return this.vizrtHealth?.mse?.healthy || false;
    },
    playlistItems() {
      return (this.playlists || []).map((p) => ({
        text: p.dsc_nome_playlist,
        value: p.id_playlist,
        ativa: !!p.flg_ativa,
      }));
    },
    currentPlaylist() {
      if (!this.selectedPlaylistId) return null;
      return this.playlists.find((p) => p.id_playlist === this.selectedPlaylistId);
    },
    // Cenas already on this playlist — the add drawer greys them out.
    currentPlaylistCenaIds() {
      return (this.currentPlaylist?.items || [])
        .map((i) => i.id_gh_cena)
        .filter((id) => id != null);
    },
    isActive() {
      return this.currentPlaylist?.flg_ativa || false;
    },
    // MOS playlists are newsroom-authoritative: their story structure is controlled
    // upstream, so no "new story" action for them.
    isMos() {
      return this.currentPlaylist?.dsc_origem === "MOS";
    },
    engineBusyMessage() {
      const c = this.engineBusyConflict;
      if (!c) return "";
      const nome = c.dscProducao || `produção #${c.codProducao}`;
      return `A produção "${nome}" está ativa no engine. Ativar esta playlist vai desativar o playout dela e colocar o seu no lugar.`;
    },
  },
  // vizrtHealth is fetched by GloboGraphics view (always rendered alongside this
  // panel); we just read from the shared Vuex state.
  mounted() {
    this.startRefreshLoop();
    document.addEventListener("visibilitychange", this.onVisibilityChange);
  },
  beforeDestroy() {
    this.stopRefreshLoop();
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
  },
  watch: {
    cod_producao: {
      immediate: true,
      async handler(val) {
        // Token guards against rapid production switches: if the user picks
        // A → B before A's fetch resolves, the stale A response would land
        // after B's reset and clobber the panel. Skip apply when token moved.
        const myToken = ++this.switchToken;

        // Reset every piece of local state so nothing leaks across productions
        this.selectedPlaylistId = null;
        this.engineBusyConflict = null;
        this.showEngineBusyModal = false;
        this.showAddSceneDialog = false;
        this.showCreateDialog = false;
        this.showDeletePlaylistDialog = false;
        this.isDragging = false;
        this.persistingReorder = false;
        this.refreshFailureCount = 0;
        // Wipe the previous production's playout state so the panel can't
        // flash stale playlists during the in-flight fetch. Polling tick
        // does NOT clear (would cause every-10s flicker) — only this
        // production-switch path does.
        this.clearPlayoutForProductionSwitch();

        if (!val) return;
        try {
          await this.fetchPlaylists(Number(val));
          if (myToken !== this.switchToken) return;
          await this.fetchPlayoutState(Number(val));
          if (myToken !== this.switchToken) return;
          if (this.activePlaylist) {
            this.selectedPlaylistId = this.activePlaylist.id_playlist;
          }
        } catch (e) {
          // Error surfaces via Vuex lastError alert
        }
      },
    },
    // Switching playlists drops any big-preview selection so it can't keep showing
    // an item that's no longer in view.
    selectedPlaylistId() {
      this.$store.dispatch("playout/clearPreviewSelection");
    },
  },
  methods: {
    ...mapActions({
      fetchPlaylists: "playout/fetchPlaylists",
      fetchPlayoutState: "playout/fetchPlayoutState",
      clearPlayoutForProductionSwitch: "playout/clearPlayoutForProductionSwitch",
      deletePlaylist: "playout/deletePlaylist",
      removeItemFromPlaylist: "playout/removeItemFromPlaylist",
      reorderPlaylistItems: "playout/reorderPlaylistItems",
      createGrupo: "playout/createGrupo",
      updateGrupo: "playout/updateGrupo",
      deleteGrupo: "playout/deleteGrupo",
      reorderGrupos: "playout/reorderGrupos",
      reorderGrupoItems: "playout/reorderGrupoItems",
      moveItemToGrupo: "playout/moveItemToGrupo",
      activatePlaylist: "playout/activatePlaylist",
      deactivatePlaylist: "playout/deactivatePlaylist",
      playlistTakeItem: "playout/playlistTakeItem",
    }),

    // Select the playlist that was just created so the panel shows its contents.
    onPlaylistCreated(idPlaylist) {
      if (idPlaylist) this.selectedPlaylistId = idPlaylist;
    },

    handleDeletePlaylist() {
      if (!this.selectedPlaylistId) return;
      this.showDeletePlaylistDialog = true;
    },
    async confirmDeletePlaylist() {
      try {
        await this.deletePlaylist({
          idPlaylist: this.selectedPlaylistId,
          codProducao: this.cod_producao,
        });
        this.selectedPlaylistId = null;
      } catch (_) {
        // Error surfaces via Vuex lastError alert
      } finally {
        this.showDeletePlaylistDialog = false;
      }
    },

    async handleRemoveItem(item) {
      await this.removeItemFromPlaylist({
        idPlaylist: this.selectedPlaylistId,
        idItem: item.id_playlist_item,
        codProducao: this.cod_producao,
      });
    },

    // Drag/drop bridges from PlaylistItemList → parent state
    onDragStateChange(isDragging) {
      this.isDragging = isDragging;
    },
    // Reorder within a section: ungrouped (idGrupo null) → playlist reorder;
    // a story → group-item reorder.
    async onReorderSection({ idGrupo, items }) {
      this.persistingReorder = true;
      try {
        if (idGrupo == null) {
          await this.reorderPlaylistItems({
            idPlaylist: this.selectedPlaylistId,
            items: items.map((it, idx) => ({
              id_playlist_item: it.id_playlist_item,
              num_ordem: idx + 1,
            })),
            codProducao: this.cod_producao,
          });
        } else {
          await this.reorderGrupoItems({
            idGrupo,
            order: items.map((it) => it.id_playlist_item),
            codProducao: this.cod_producao,
          });
        }
      } finally {
        this.persistingReorder = false;
      }
    },
    async handleMoveItem({ item, idGrupo }) {
      await this.moveItemToGrupo({
        idPlaylist: this.selectedPlaylistId,
        idItem: item.id_playlist_item,
        idGrupo,
        codProducao: this.cod_producao,
      });
    },
    async handleCreateGroup() {
      await this.createGrupo({
        idPlaylist: this.selectedPlaylistId,
        titulo: "Nova pasta",
        codProducao: this.cod_producao,
      });
    },
    async handleRenameGroup({ idGrupo, titulo }) {
      await this.updateGrupo({ idGrupo, titulo, codProducao: this.cod_producao });
    },
    async handleDeleteGroup(idGrupo) {
      await this.deleteGrupo({ idGrupo, codProducao: this.cod_producao });
    },
    async handleReorderGroups(order) {
      await this.reorderGrupos({
        idPlaylist: this.selectedPlaylistId,
        order,
        codProducao: this.cod_producao,
      });
    },
    onMosImported(idPlaylist) {
      // The import action already refetched playlists; select the new MOS playlist.
      if (idPlaylist) this.selectedPlaylistId = idPlaylist;
    },

    // The toast holds while the condition does — like the offline one — so it needs a
    // way out: an operator who has read it should be able to clear it without
    // deactivating the rundown. The next command clears it anyway.
    dismissActivationReport() {
      this.$store.commit("playout/setActivationReport", null);
    },

    async handleActivate(force = false) {
      try {
        await this.activatePlaylist({
          idPlaylist: this.selectedPlaylistId,
          codProducao: this.cod_producao,
          force,
        });
      } catch (err) {
        const payload = err.response?.data;
        if (payload?.code === "ENGINE_BUSY" && payload.conflict) {
          this.engineBusyConflict = payload.conflict;
          this.showEngineBusyModal = true;
        }
        // other errors are already captured into Vuex lastError and shown in the alert
      }
    },

    async handleForceActivate() {
      try {
        await this.handleActivate(true);
      } catch (_) {
        // Inner handleActivate already routes errors to lastError; nothing
        // to do here besides the modal/conflict cleanup in `finally`.
      } finally {
        this.showEngineBusyModal = false;
        this.engineBusyConflict = null;
      }
    },

    cancelEngineBusy() {
      this.showEngineBusyModal = false;
      this.engineBusyConflict = null;
    },

    // --- Cross-session state freshness ---
    // Another operator may force-activate over this production's playlist
    // (ENGINE_BUSY override), deactivating it server-side. No push channel,
    // so we poll own state periodically + refresh on tab focus.
    //
    // Salvas (saved scenes) is NOT refreshed on every tick — that would
    // replace the whole list and cause a visible re-render. Instead we only
    // dispatch a Salvas refresh when we detect our own active playlist was
    // taken from us (wasActive → notActive transition), which is the only
    // case where Salvas chips go stale.
    startRefreshLoop() {
      this.stopRefreshLoop();
      this.refreshTimer = setInterval(() => {
        if (document.hidden) return;
        if (this.isDragging) return; // don't interrupt drag-to-reorder
        this.refreshPanelState();
      }, 10000);
    },
    stopRefreshLoop() {
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer);
        this.refreshTimer = null;
      }
    },
    onVisibilityChange() {
      if (document.hidden) return;
      // Tab just gained focus — user expects fresh state, do full refresh.
      this.refreshPanelState({ refreshSalvas: true });
    },
    async refreshPanelState({ refreshSalvas = false } = {}) {
      if (!this.cod_producao) return;
      const wasActive = !!this.activePlaylist;
      try {
        await Promise.all([
          this.fetchPlaylists(Number(this.cod_producao)),
          this.fetchPlayoutState(Number(this.cod_producao)),
        ]);
        this.refreshFailureCount = 0;
      } catch (_) {
        this.refreshFailureCount++;
        // Surface a one-shot warning after 3 consecutive poll failures so the
        // operator knows the panel state may be stale.
        if (this.refreshFailureCount === 3) {
          this.$toast?.warning?.("Conexão instável com o servidor de playout");
        }
        return;
      }

      const stillActive = !!this.activePlaylist;
      // If we were active and now we're not, someone else took the engine.
      // Salvas chips (MSE / No Ar) are now stale — dispatch a one-shot refresh.
      if (wasActive && !stillActive) {
        refreshSalvas = true;
      }
      if (refreshSalvas) {
        this.$store
          .dispatch("globographics/loadSavedScenes", {
            codProducao: Number(this.cod_producao),
          })
          .catch(() => {});
      }
    },

    async handleDeactivate() {
      await this.deactivatePlaylist({
        idPlaylist: this.selectedPlaylistId,
        codProducao: this.cod_producao,
      });
    },

    // Take a SPECIFIC item straight to air (per-item play button) — avoids clicking
    // Next repeatedly through a long MOS playlist.
    async handleTakeItem(item) {
      if (!item) return;
      try {
        await this.playlistTakeItem({
          idPlaylist: this.selectedPlaylistId,
          idItem: item.id_playlist_item,
          codProducao: this.cod_producao,
        })
      } catch (err) {
        this.$toast?.error?.(
          err.response?.data?.error || "Erro ao executar Take do item"
        );
      }
    },
  },
};
</script>

<style lang="scss" scoped>
// Accent-text button (Adicionar Cena).
.gg-text-accent {
  color: var(--sc-gg-accent) !important;
  text-transform: none;
  letter-spacing: normal;

  ::v-deep .v-icon {
    color: var(--sc-gg-accent) !important;
  }
}

// ── Subheader: playlist select + accent icon buttons ─────────────────────
.gg-pl-sub {
  display: flex;
  align-items: center;
  gap: var(--sc-space-4);
  width: 100%;
}

.gg-pl-select {
  flex: 1 1 auto;
  min-width: 0;
}

.gg-pl-select ::v-deep {
  .v-input__slot {
    min-height: 56px !important;
    padding: 0 var(--sc-space-3) !important;
    background: var(--sc-gg-surface-2) !important;
    border: 1px solid var(--sc-gg-border-100);
    border-radius: var(--sc-radius-md);
    box-shadow: none !important;
  }
  .v-select__selections {
    flex-wrap: nowrap;
    min-width: 0;
  }
  input::placeholder {
    color: var(--sc-gg-text-tertiary) !important;
  }
  .v-icon {
    color: var(--sc-gg-text-secondary) !important;
  }
}

.gg-pl-select--disabled ::v-deep {
  .v-input__slot {
    background: var(--sc-gg-surface-3) !important;
  }
  input::placeholder {
    color: var(--sc-gg-disabled) !important;
  }
  .v-icon {
    color: var(--sc-gg-disabled) !important;
  }
}

.gg-pl-select__val {
  overflow: hidden;
  color: var(--sc-gg-text-primary);
  font-size: 14px;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.gg-pl-select__opt {
  color: var(--sc-gg-text-strong);
  font-size: 14px;
}

.gg-pl-iconbtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border: 1px solid var(--sc-gg-accent);
  border-radius: var(--sc-radius-lg);
  color: var(--sc-gg-accent);
  transition: background var(--sc-duration-fast) var(--sc-easing-standard);

  .v-icon {
    color: var(--sc-gg-accent) !important;
  }

  &:hover {
    background: rgba(var(--sc-gg-accent-rgb), 0.12);
  }
}

// ── MOS rundown nav row (fixed above the scrolling list) ─────────────────
// Only the item list scrolls; pulled to the module edges so the scrollbar sits
// at the edge (the list rows carry their own inset).
.gg-pl-scroll {
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
  margin: 0 calc(-1 * var(--sc-space-4));

  scrollbar-width: thin;
  scrollbar-color: var(--sc-gg-border-200) transparent;

  &::-webkit-scrollbar {
    display: block;
    width: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--sc-gg-border-200);
    border-radius: var(--sc-radius-pill);
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
}

.gg-pl-activate {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  min-width: 72px;
  padding: 0 var(--sc-space-3);
  flex-shrink: 0;
  border: 1px solid var(--sc-gg-border-100);
  border-radius: var(--sc-radius-md);
  color: var(--sc-gg-accent);
  font-size: 16px;
  font-weight: var(--sc-weight-medium);
  letter-spacing: 0.16px;
  white-space: nowrap;
  transition: background var(--sc-duration-fast) var(--sc-easing-standard);

  &:hover {
    background: var(--sc-gg-overlay-1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
}

.gg-pl-activate--stop {
  color: var(--sc-gg-critical);
}
</style>

<!-- Non-scoped: the select's option slot renders inside the teleported menu,
     which carries no scope attribute, so the badge must be reachable there too. -->
<style lang="scss">
.gg-pl-select__badge {
  flex-shrink: 0;
  margin-left: var(--sc-space-2);
  color: var(--sc-gg-accent);
  font-size: 14px;
  font-weight: var(--sc-weight-medium);
  line-height: 16px;
  letter-spacing: 0.07px;
  white-space: nowrap;
}
</style>
